/**
 * Venue floor plans, as data.
 *
 * A dinner-show room is not interchangeable seating: at Léopard the table you
 * get decides whether you are in front of the dancers or across the room from
 * them, and guests ask us which zone to book. The plan answers that on the
 * page instead of over WhatsApp.
 *
 * Geometry lives here rather than in either renderer because the web app draws
 * it with `<svg>` and the Expo app with `react-native-svg`. Same numbers, two
 * renderers — if the coordinates lived in the components they would drift the
 * first time somebody nudged one of them.
 *
 * Coordinates are on a SQUARE canvas (`size` × `size`) so the plan keeps the
 * room's real proportions at any width; both renderers set
 * `viewBox="0 0 size size"` and let the container decide how big it is.
 *
 * Zone titles and descriptions are venue copy, so they carry all seven
 * SUPPORTED_LANGS here. The furniture labels (bar lounge, DJ, reception) and
 * the section heading are generic chrome and come from each app's i18n
 * catalogue instead.
 */

import { pickContentLang, type Lang } from "../i18n/langs";

/** A translatable string on a plan. Every SUPPORTED_LANGS key is required. */
export type FloorPlanText = Record<Lang, string>;

export interface FloorPlanRect {
  x: number;
  y: number;
  w: number;
  h: number;
  /** Corner radius. */
  r: number;
}

export interface FloorPlanZone {
  /** Stable key. Used for React keys and, later, for the reservation note. */
  id: string;
  rect: FloorPlanRect;
  /** Fill of the zone block, and of its swatch in the legend. */
  fill: string;
  /** Ink for the label drawn inside the block. */
  ink: string;
  /** Drawn inside the block, one line per entry ("ZONE 1", "SHOW"). */
  planLabel: FloorPlanText[];
  /** Legend heading. */
  title: FloorPlanText;
  /** Legend body. */
  description: FloorPlanText;
}

export interface FloorPlan {
  /** Width and height of the square canvas the coordinates are drawn on. */
  size: number;
  zones: FloorPlanZone[];
  /** The bar counter outline, drawn inside the first zone. */
  bar: FloorPlanRect;
  /** Seats along the front of the bar. Top-left of each chair glyph. */
  barChairs: { x: number; y: number }[];
  /** Stools at the two ends of the bar. Top-left of each stool glyph. */
  barStools: { x: number; y: number }[];
  /** The DJ booth, inside the show zone. */
  dj: FloorPlanRect;
  /** The reception desk, boxed off from the room. */
  reception: FloorPlanRect;
}

/** Ink and fills shared by the plan's furniture. */
export const FLOOR_PLAN_COLORS = {
  /** Chairs, stools and the bar outline. */
  fixture: "#9a7c55",
  barInk: "#7a5f3c",
  djFill: "#E0BCB8",
  djInk: "#7a4340",
  receptionFill: "#ffffff",
  receptionEdge: "#e4e4ef",
  receptionInk: "#5a5d77",
  /** The pad the plan sits on, so it reads as a drawing and not as page. */
  canvas: "#faf9fc",
} as const;

export const LEOPARD_FLOOR_PLAN: FloorPlan = {
  size: 1000,
  zones: [
    {
      id: "restaurant",
      rect: { x: 13, y: 9, w: 761, h: 593, r: 22 },
      fill: "#EFE6D8",
      ink: "#5b4a33",
      planLabel: [
        {
          en: "ZONE 2",
          fr: "ZONE 2",
          ar: "المنطقة 2",
          es: "ZONA 2",
          it: "ZONA 2",
          de: "ZONE 2",
          nl: "ZONE 2",
        },
        {
          en: "RESTAURANT",
          fr: "RESTAURANT",
          ar: "المطعم",
          es: "RESTAURANTE",
          it: "RISTORANTE",
          de: "RESTAURANT",
          nl: "RESTAURANT",
        },
      ],
      title: {
        en: "Zone 2 · Restaurant",
        fr: "Zone 2 · Restaurant",
        ar: "المنطقة 2 · المطعم",
        es: "Zona 2 · Restaurante",
        it: "Zona 2 · Ristorante",
        de: "Zone 2 · Restaurant",
        nl: "Zone 2 · Restaurant",
      },
      description: {
        en: "The main room, set back from the stage with a clear view of it, with the bar lounge at the top.",
        fr: "La salle principale, en retrait de la scène mais avec une vue dégagée, avec le bar lounge en haut de la salle.",
        ar: "القاعة الرئيسية، بعيدة قليلاً عن المسرح مع رؤية واضحة له، وفي أعلاها البار لاونج.",
        es: "El comedor principal, algo retirado del escenario pero con vista despejada, con el bar lounge en la parte alta de la sala.",
        it: "La sala principale, arretrata rispetto al palco ma con vista libera, con il bar lounge in fondo alla sala.",
        de: "Der Hauptspeisesaal, etwas von der Bühne abgesetzt, aber mit freier Sicht, mit der Bar-Lounge am oberen Ende des Raums.",
        nl: "De hoofdzaal, iets verder van het podium maar met vrij zicht, met de barlounge boven in de zaal.",
      },
    },
    {
      id: "show",
      rect: { x: 13, y: 602, w: 761, h: 396, r: 22 },
      fill: "#E9CFCC",
      ink: "#7a4340",
      planLabel: [
        {
          en: "ZONE 1",
          fr: "ZONE 1",
          ar: "المنطقة 1",
          es: "ZONA 1",
          it: "ZONA 1",
          de: "ZONE 1",
          nl: "ZONE 1",
        },
        {
          en: "SHOW",
          fr: "SPECTACLE",
          ar: "العرض",
          es: "ESPECTÁCULO",
          it: "SPETTACOLO",
          de: "SHOW",
          nl: "SHOW",
        },
      ],
      title: {
        en: "Zone 1 · Show floor",
        fr: "Zone 1 · Espace spectacle",
        ar: "المنطقة 1 · مساحة العرض",
        es: "Zona 1 · Espacio del espectáculo",
        it: "Zona 1 · Spazio spettacolo",
        de: "Zone 1 · Showfläche",
        nl: "Zone 1 · Showvloer",
      },
      description: {
        en: "The tables right in front of the stage and the DJ booth. Book here if you want to be closest to the performances.",
        fr: "Les tables juste devant la scène et la cabine du DJ. Réservez ici pour être au plus près des artistes.",
        ar: "الطاولات مباشرة أمام المسرح وكابينة الدي جي. احجز هنا إن أردت أقرب مكان من الفقرات.",
        es: "Las mesas justo delante del escenario y la cabina del DJ. Reserva aquí si quieres estar lo más cerca posible de las actuaciones.",
        it: "I tavoli proprio davanti al palco e alla consolle del DJ. Prenota qui se vuoi stare il più vicino possibile agli artisti.",
        de: "Die Tische direkt vor der Bühne und dem DJ-Pult. Reservieren Sie hier, wenn Sie den Auftritten am nächsten sein möchten.",
        nl: "De tafels vlak voor het podium en de dj-booth. Reserveer hier als je zo dicht mogelijk bij de optredens wilt zitten.",
      },
    },
    {
      id: "terrace",
      rect: { x: 784, y: 255, w: 216, h: 740, r: 22 },
      /* The venue's own plan paints the terrace the same beige as the
         restaurant, which leaves the legend with two identical swatches and
         nothing to match them to. Green keeps the zones distinguishable while
         the label still says Zone 2. */
      fill: "#E2E8DC",
      ink: "#4a5a44",
      planLabel: [
        {
          en: "ZONE 2",
          fr: "ZONE 2",
          ar: "المنطقة 2",
          es: "ZONA 2",
          it: "ZONA 2",
          de: "ZONE 2",
          nl: "ZONE 2",
        },
        {
          en: "TERRACE",
          fr: "TERRASSE",
          ar: "التراس",
          es: "TERRAZA",
          it: "TERRAZZA",
          de: "TERRASSE",
          nl: "TERRAS",
        },
      ],
      title: {
        en: "Zone 2 · Terrace",
        fr: "Zone 2 · Terrasse",
        ar: "المنطقة 2 · التراس",
        es: "Zona 2 · Terraza",
        it: "Zona 2 · Terrazza",
        de: "Zone 2 · Terrasse",
        nl: "Zone 2 · Terras",
      },
      description: {
        en: "The outdoor stretch along the side of the restaurant, open for lunch and early dinner.",
        fr: "L'espace extérieur qui longe le côté du restaurant, ouvert pour le déjeuner et le dîner tôt.",
        ar: "المساحة الخارجية الممتدة على جانب المطعم، مفتوحة للغداء والعشاء المبكر.",
        es: "La zona exterior que recorre el lateral del restaurante, abierta para el almuerzo y la cena temprana.",
        it: "Lo spazio esterno lungo il lato del ristorante, aperto per il pranzo e la cena anticipata.",
        de: "Der Außenbereich an der Seite des Restaurants, geöffnet für Mittag- und frühes Abendessen.",
        nl: "De buitenruimte langs de zijkant van het restaurant, open voor de lunch en het vroege diner.",
      },
    },
  ],
  bar: { x: 57, y: 35, w: 293, h: 143, r: 8 },
  barChairs: [
    { x: 80, y: 196 },
    { x: 124, y: 196 },
    { x: 168, y: 196 },
    { x: 212, y: 196 },
    { x: 256, y: 196 },
    { x: 300, y: 196 },
  ],
  barStools: [
    { x: 33, y: 72 },
    { x: 33, y: 112 },
    { x: 366, y: 72 },
    { x: 366, y: 112 },
  ],
  dj: { x: 638, y: 869, w: 136, h: 126, r: 10 },
  reception: { x: 787, y: 5, w: 213, h: 238, r: 14 },
};

/**
 * Slug → plan. Léopard is the only venue with one today; the section renders
 * nothing for everyone else, so it is safe to mount on every activity page.
 */
export const FLOOR_PLANS: Readonly<Record<string, FloorPlan>> = {
  "leopard-marrakech": LEOPARD_FLOOR_PLAN,
};

export function getFloorPlan(slug: string | null | undefined): FloorPlan | null {
  if (!slug) return null;
  return FLOOR_PLANS[slug] ?? null;
}

/** Falls back to English so a half-filled plan never renders an empty label. */
export function pickFloorPlanText(text: FloorPlanText, lang: string | undefined): string {
  return text[pickContentLang(lang)]?.trim() || text.en;
}
